import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { Epic, EpicProgress, EpicRoadmapData } from 'shared/types/epic';
import { BugReport } from 'shared/types/bug-report';
import {
  getEpicRoadmap,
  deleteEpic
} from 'shared/services/epicService';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { EpicDetailHeader } from './components/EpicDetailHeader';
import { EpicFeaturesKanban } from './components/EpicFeaturesKanban';
import { EditEpicModal } from './components/EditEpicModal';
import { AddFeatureToEpicModal } from './components/AddFeatureToEpicModal';
import { AddBugReportModal } from '../components/AddBugReportModal';
import * as Sentry from '@sentry/react';

export const EpicDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { session } = useSession();
  const navigate = useNavigate();

  const [epic, setEpic] = useState<Epic | null>(null);
  const [progress, setProgress] = useState<EpicProgress | null>(null);
  const [features, setFeatures] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddFeatureModalOpen, setIsAddFeatureModalOpen] = useState(false);
  const [isCreateFeatureModalOpen, setIsCreateFeatureModalOpen] = useState(false);

  // Fetch epic and its roadmap data
  const fetchEpicData = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const roadmapData: EpicRoadmapData = await getEpicRoadmap(
        parseInt(id),
        session
      );
      setEpic(roadmapData.epic);
      setProgress(roadmapData.progress);
      setFeatures(roadmapData.epic.features || []);
    } catch (error) {
      console.error('Error fetching epic data:', error);
      Sentry.captureException(error, {
        tags: { screen: 'EpicDetail' },
        extra: { epicId: id }
      });
      toast.error('Fout bij laden epic');
      navigate('/bug-reports/epics');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all bug reports to populate features list
  const fetchAllFeatures = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/bug-reports`,
        { method: 'GET' },
        session
      );

      if (response.ok) {
        const allBugReports: BugReport[] = await response.json();
        setFeatures(allBugReports);
      }
    } catch (error) {
      console.error('Error fetching features:', error);
      Sentry.captureException(error, {
        tags: { screen: 'EpicDetail' }
      });
    }
  };

  useEffect(() => {
    fetchEpicData();
    fetchAllFeatures();
  }, [id, session]);

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = async () => {
    if (!epic) return;

    const confirmDelete = window.confirm(
      `Weet je zeker dat je "${epic.title}" wilt verwijderen? Alle features worden ontkoppeld van deze epic.`
    );

    if (!confirmDelete) return;

    try {
      await deleteEpic(epic.id, session);
      toast.success('Epic succesvol verwijderd');
      navigate('/bug-reports/epics');
    } catch (error: any) {
      console.error('Error deleting epic:', error);
      Sentry.captureException(error, {
        tags: { screen: 'EpicDetail' },
        extra: { epicId: epic.id }
      });
      toast.error(
        `Fout bij verwijderen epic: ${error.message || 'Onbekende fout'}`
      );
    }
  };

  const handleFeatureClick = (feature: BugReport) => {
    navigate(`/bug-reports/${feature.id}`);
  };

  const handleUpdateSuccess = () => {
    fetchEpicData();
    fetchAllFeatures();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-neutral-500">Laden...</div>
        </div>
      </div>
    );
  }

  if (!epic || !progress) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-neutral-500">Epic niet gevonden</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Epic Header */}
      <EpicDetailHeader
        epic={epic}
        progress={progress}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onCreateFeature={() => setIsCreateFeatureModalOpen(true)}
      />

      {/* Features Kanban */}
      <EpicFeaturesKanban
        features={features}
        epicId={epic.id}
        onUpdate={handleUpdateSuccess}
        onFeatureClick={handleFeatureClick}
        onAddFeature={() => setIsAddFeatureModalOpen(true)}
      />

      {/* Edit Modal */}
      <EditEpicModal
        isOpen={isEditModalOpen}
        epic={epic}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={handleUpdateSuccess}
      />

      {/* Add Feature Modal */}
      <AddFeatureToEpicModal
        isOpen={isAddFeatureModalOpen}
        epicId={epic.id}
        epicTitle={epic.title}
        onClose={() => setIsAddFeatureModalOpen(false)}
        onSuccess={handleUpdateSuccess}
      />

      {/* Create Feature Modal */}
      <AddBugReportModal
        isOpen={isCreateFeatureModalOpen}
        onClose={() => setIsCreateFeatureModalOpen(false)}
        onSuccess={handleUpdateSuccess}
        preselectedEpicId={epic.id}
      />
    </div>
  );
};
